import DashboardContainer from "components/dashboard/DashboardContainer";
import { Header } from "components/DesignSystem/Header/Header";
import Tab from "components/DesignSystem/Tab/Tab";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { Bills } from "./Bills";
import { SendBillPage } from "components/VendorBills/SendBillPage";
import { AddBillPage } from "components/VendorBills/AddBillPage";
import { RequestBillPage } from "components/VendorBills/RequestBillPage";
import { AddRequestAndBill } from "components/VendorBills/AddRequestAndBill/AddRequestAndBill";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowDown } from "components/icons/ArrowDown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import {
  useLazyGetAllBillsQuery,
  useGetAllBillsQuery,
} from "store/apis/vendorBills";
import { useToast } from "hooks/useToast";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { openLink } from "utils/openLink";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useSuggestTransactionsMutation } from "store/apis/invoices";
import ToolTip from "components/design/toolTip";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { RadianButton } from "components/DesignSystem/RadianButton/RadianButton";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

export const BillsAndVendors = () => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const merchant_id = undefined;
  const canRequestAndAddBill = true;
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const { link } = useConstructInternalLink();
  const { successToast, alertToast, info: infoToast } = useToast();
  const [exportBills, { isLoading: isExporting }] = useLazyGetAllBillsQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const [suggestTransactions, { isLoading: isSuggestingTransactions }] =
    useSuggestTransactionsMutation();

  const { data: billsData } = useGetAllBillsQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
      bill_type: FINANCIAL_RECORD_TYPE.BILL,
      page_num: 1,
      search_term: null,
      limit: 1,
    },
    { skip: !groupId || !entityId }
  );

  dayjs.extend(relativeTime);
  const getRelativeTime = (timestamp?: string | null) => {
    if (!timestamp) return null;
    return dayjs(timestamp).fromNow();
  };

  const relativeRefreshTime = getRelativeTime(
    billsData?.last_invoice_transaction_suggestion_run_at
  );

  const onExport = async ({ exportToMail }: { exportToMail: boolean }) => {
    try {
      const exportBillsData = await exportBills({
        page_num: 1,
        groupId: groupId!,
        entityId: entityId!,
        bill_type: FINANCIAL_RECORD_TYPE.BILL,
        search_term: null,
        ids: undefined,
        minAmount: undefined,
        maxAmount: undefined,
        startDate: "",
        endDate: "",
        dueDateStartDate: "",
        dueDateEndDate: "",
        vendorIds: undefined,
        statuses: undefined,
        sources: undefined,
        merchant_id,
        generate_report: true,
        export_to_email: exportToMail,
      }).unwrap();

      if (exportToMail) {
        return successToast({
          message: "Bills export initiated. You will receive an email shortly",
        });
      }

      if (exportBillsData.excel_document_id) {
        const { download_url } = await getPreviewUrl({
          groupId: groupId!,
          fileId: exportBillsData.excel_document_id,
        }).unwrap();

        openLink(download_url);
      }

      successToast({
        message: "Bills exported successfully",
      });
    } catch (error) {
      alertToast({
        message: "Error exporting bills",
      });
    }
  };

  const onSuggestTransactions = async () => {
    if (!groupId || !entityId) return;

    try {
      await suggestTransactions({
        groupId,
        entityId,
      }).unwrap();

      infoToast({
        title: "Refreshing",
        message:
          "Updating bills suggestions. We'll notify you when they are ready",
      });
    } catch (error) {
      alertToast({
        message: "Error refreshing suggested transactions",
      });
    }
  };

  return (
    <Tab.Root asChild defaultValue={pathname} value="MATCH">
      <DashboardContainer className="t-h-full">
        <DashboardContainer.Header className="t-px-8 t-z-header">
          <div className="t-sticky t-top-0">
            <Header
              v2
              title="Bills"
              right={
                <div className="t-flex t-items-center t-gap-2 t-my-1">
                  <ToolTip
                    text={
                      <div className="t-text-left t-w-40">
                        <div>Refresh AI suggestions for bill payments</div>
                        {relativeRefreshTime && (
                          <div className="t-italic t-mt-2 t-text-caption-italic">
                            Last refreshed {relativeRefreshTime}
                          </div>
                        )}
                      </div>
                    }
                    side="bottom"
                  >
                    <span>
                      <RadianButton
                        onClick={onSuggestTransactions}
                        className="!t-mt-0"
                        size="small"
                      >
                        {""}
                      </RadianButton>
                    </span>
                  </ToolTip>
                  <div className="t-flex t-gap-2">
                    <Dropdown.Root>
                      <Dropdown.Trigger asChild className="t-group">
                        <div>
                          <Button
                            size="xsmall"
                            onClick={(e) => e.stopPropagation()}
                            isLoading={isExporting}
                            disabled={isExporting}
                          >
                            <div className="t-flex t-items-center t-gap-1.5">
                              Export
                              <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                                <ArrowDown color="currentColor" />
                              </div>
                            </div>
                          </Button>
                        </div>
                      </Dropdown.Trigger>
                      <Dropdown.Portal>
                        <Dropdown.Content
                          sideOffset={4}
                          align="end"
                          side="bottom"
                        >
                          <ExportOptions
                            onLocalDownload={() =>
                              onExport({ exportToMail: false })
                            }
                            onMailExport={() =>
                              onExport({ exportToMail: true })
                            }
                          />
                        </Dropdown.Content>
                      </Dropdown.Portal>
                    </Dropdown.Root>
                  </div>
                  {canRequestAndAddBill && (
                    <LockedUi
                      feature={WRITE_PERMISSION_KEYS.EXPENSE}
                      blockedUi={<LockedButton text="Add bill" size="xsmall" />}
                    >
                      <AddRequestAndBill />
                    </LockedUi>
                  )}
                </div>
              }
            />
          </div>
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-mb-6 !t-overflow-hidden">
          <div className="t-px-5 t-h-full t-w-full">
            <Switch>
              <Route exact path={path}>
                <Bills canRequestAndAddBill={canRequestAndAddBill} />
              </Route>
              <Route path={`${path}/request/:billId`}>
                <RequestBillPage
                  close={() =>
                    history.push(
                      link(url, {
                        moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.REQUEST },
                      })
                    )
                  }
                />
              </Route>
              <Route path={`${path}/send/:billId`}>
                <SendBillPage close={() => history.goBack()} />
              </Route>
              <Route path={`${path}/bill/:billId`}>
                <AddBillPage
                  close={() =>
                    history.push(
                      link(url, {
                        moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL },
                      })
                    )
                  }
                />
              </Route>
            </Switch>
          </div>
        </DashboardContainer.Content>
      </DashboardContainer>
    </Tab.Root>
  );
};
